import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import {
  XMarkIcon,
  PhotoIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { galleryApi, CreateGalleryItemData } from '../services/galleryApi'
import { eventsApi } from '../services/eventsApi'
import { useUsersApi } from '../services/usersApi'
import { useLocationsApi } from '../services/locationsApi'
import toast from 'react-hot-toast'

interface UploadMediaModalProps {
  isOpen: boolean
  onClose: () => void
  onUploadSuccess: () => void
}

interface UploadProgress {
  fileName: string
  progress: number
  status: 'uploading' | 'processing' | 'success' | 'error'
  error?: string
}

export default function UploadMediaModal({ isOpen, onClose, onUploadSuccess }: UploadMediaModalProps) {
  const { getAccessTokenSilently } = useAuth0()
  const usersApi = useUsersApi()
  const locationsApi = useLocationsApi()
  const [dragActive, setDragActive] = useState(false)
  const [uploads, setUploads] = useState<UploadProgress[]>([])
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tags: {
      events: [] as string[],
      years: [] as number[],
      members: [] as string[],
      locations: [] as string[],
      scheduledEvents: [] as string[]
    }
  })
  const [currentStep, setCurrentStep] = useState<'upload' | 'details' | 'uploading'>('upload')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch real data from APIs
  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => eventsApi.getEvents(getAccessTokenSilently),
    enabled: isOpen && !!getAccessTokenSilently
  })

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.getUsers(getAccessTokenSilently),
    enabled: isOpen && !!getAccessTokenSilently
  })

  const { data: locationsData, isLoading: locationsLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationsApi.getLocations(getAccessTokenSilently),
    enabled: isOpen && !!getAccessTokenSilently
  })

  // Check if all tag data is loaded
  const isTagDataLoading = eventsLoading || membersLoading || locationsLoading
  const isTagDataReady = !isTagDataLoading && eventsData && membersData && locationsData

  // Transform API data into tag options
  const tagOptions = {
    events: eventsData?.events?.map(event => ({
      id: event.title, // Use title as ID for consistency
      name: event.title
    })) || [],
    locations: locationsData?.locations?.map(location => ({
      id: location.name, // Use name as ID for consistency
      name: location.name
    })) || [],
    members: membersData?.users?.map(user => {
      const displayName = `${user.firstName} ${user.lastName}`.trim() || user.email
      return {
        id: displayName, // Use display name as ID for consistency
        name: displayName
      }
    }) || []
  }

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setCurrentStep('upload')
      setSelectedFiles([])
      setUploads([])
      setFormData({
        title: '',
        description: '',
        tags: {
          events: [],
          years: [],
          members: [],
          locations: [],
          scheduledEvents: []
        }
      })
    }
  }, [isOpen])

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files))
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    if (e.target.files && e.target.files[0]) {
      handleFiles(Array.from(e.target.files))
    }
  }

  const handleFiles = (files: File[]) => {
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/')
      const isValidSize = file.size <= 50 * 1024 * 1024 // 50MB limit
      
      if (!isImage && !isVideo) {
        toast.error(`${file.name} is not a valid image or video file`)
        return false
      }
      
      if (!isValidSize) {
        toast.error(`${file.name} is too large. Maximum size is 50MB`)
        return false
      }
      
      return true
    })

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles)
      setCurrentStep('details')
      
      // Set default title if only one file
      if (validFiles.length === 1) {
        setFormData(prev => ({
          ...prev,
          title: validFiles[0].name.split('.')[0]
        }))
      }
    }
  }

  const handleTagToggle = (category: keyof typeof formData.tags, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      tags: {
        ...prev.tags,
        [category]: prev.tags[category].includes(value as never)
          ? prev.tags[category].filter(item => item !== value)
          : [...prev.tags[category], value as never]
      }
    }))
  }

  const handleYearAdd = (year: string) => {
    const yearNum = parseInt(year)
    if (yearNum && yearNum >= 2000 && yearNum <= new Date().getFullYear() + 1) {
      if (!formData.tags.years.includes(yearNum)) {
        setFormData(prev => ({
          ...prev,
          tags: {
            ...prev.tags,
            years: [...prev.tags.years, yearNum]
          }
        }))
      }
    }
  }

  const handleUpload = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title')
      return
    }

    // Note: Years validation temporarily removed to allow existing workflow
    // if (formData.tags.years.length === 0) {
    //   toast.error('Please add at least one year')
    //   return
    // }

    setCurrentStep('uploading')
    
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i]
        
        // Initialize upload progress
        setUploads(prev => [...prev, {
          fileName: file.name,
          progress: 0,
          status: 'uploading'
        }])

        // Get upload URL
        const { uploadUrl, mediaUrl } = await galleryApi.getUploadUrl(
          file.name,
          file.type,
          getAccessTokenSilently
        )

        // Upload file with progress tracking
        await galleryApi.uploadFile(file, uploadUrl, (progress) => {
          setUploads(prev => prev.map(upload => 
            upload.fileName === file.name 
              ? { ...upload, progress }
              : upload
          ))
        })

        // Update status to processing
        setUploads(prev => prev.map(upload => 
          upload.fileName === file.name 
            ? { ...upload, status: 'processing' }
            : upload
        ))

        // Get image dimensions for images
        let width = 800
        let height = 600
        
        if (file.type.startsWith('image/')) {
          try {
            const dimensions = await getImageDimensions(file)
            width = dimensions.width
            height = dimensions.height
          } catch (error) {
            console.warn('Could not get image dimensions:', error)
          }
        }

        // Create gallery item
        const itemData: CreateGalleryItemData = {
          title: selectedFiles.length === 1 ? formData.title : `${formData.title} - ${file.name}`,
          description: formData.description,
          mediaUrl,
          mediaType: file.type.startsWith('video/') ? 'video' : 'image',
          width,
          height,
          tags: formData.tags
        }

        await galleryApi.createItem(itemData, getAccessTokenSilently)

        // Update status to success
        setUploads(prev => prev.map(upload => 
          upload.fileName === file.name 
            ? { ...upload, status: 'success', progress: 100 }
            : upload
        ))
      }

      toast.success('Media uploaded successfully!')
      onUploadSuccess()
      
      // Close modal after a brief delay
      setTimeout(() => {
        onClose()
      }, 1500)

    } catch (error) {
      console.error('Upload error:', error)
      toast.error('Failed to upload media')
      
      setUploads(prev => prev.map(upload => ({
        ...upload,
        status: 'error',
        error: error instanceof Error ? error.message : 'Upload failed'
      })))
    }
  }

  const getImageDimensions = (file: File): Promise<{ width: number, height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        resolve({ width: img.width, height: img.height })
      }
      img.onerror = reject
      img.src = URL.createObjectURL(file)
    })
  }

  const canGoNext = currentStep === 'upload' ? selectedFiles.length > 0 : formData.title.trim().length > 0

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Upload Media
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                disabled={currentStep === 'uploading'}
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {currentStep === 'upload' && (
                <div>
                  {/* File Upload Area */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      dragActive
                        ? 'border-rb-gold bg-yellow-50 dark:bg-yellow-900/20'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <PhotoIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Drop files here or click to browse
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      Supports images and videos up to 50MB
                    </p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 bg-rb-gold text-rb-dark px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors font-medium"
                    >
                      <CloudArrowUpIcon className="w-5 h-5" />
                      Choose Files
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      onChange={handleChange}
                      className="hidden"
                    />
                  </div>

                  {/* Selected Files Preview */}
                  {selectedFiles.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                        Selected Files ({selectedFiles.length})
                      </h4>
                      <div className="space-y-2">
                        {selectedFiles.map((file, index) => (
                          <div key={`${file.name}-${file.size}-${index}`} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <PhotoIcon className="w-5 h-5 text-gray-400" />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 dark:text-white">{file.name}</p>
                              <p className="text-sm text-gray-500">
                                {(file.size / (1024 * 1024)).toFixed(1)} MB
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {currentStep === 'details' && (
                <div className="space-y-6">
                  {/* Title and Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rb-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Enter a title for your media"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rb-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Optional description"
                    />
                  </div>

                  {/* Tags */}
                  <div className="space-y-4">
                    {isTagDataLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rb-gold"></div>
                        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading tags...</span>
                      </div>
                    ) : isTagDataReady ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Events */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Events
                          </label>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {tagOptions.events.length > 0 ? (
                              tagOptions.events.map(event => (
                                <label key={event.id} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={formData.tags.events.includes(event.id)}
                                    onChange={() => handleTagToggle('events', event.id)}
                                    className="mr-2 rounded"
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-300">{event.name}</span>
                                </label>
                              ))
                            ) : (
                              <p className="text-sm text-gray-500 dark:text-gray-400">No events available</p>
                            )}
                          </div>
                        </div>

                        {/* Locations */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Locations
                          </label>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {tagOptions.locations.length > 0 ? (
                              tagOptions.locations.map(location => (
                                <label key={location.id} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={formData.tags.locations.includes(location.id)}
                                    onChange={() => handleTagToggle('locations', location.id)}
                                    className="mr-2 rounded"
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-300">{location.name}</span>
                                </label>
                              ))
                            ) : (
                              <p className="text-sm text-gray-500 dark:text-gray-400">No locations available</p>
                            )}
                          </div>
                        </div>

                        {/* Members */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Members
                          </label>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {tagOptions.members.length > 0 ? (
                              tagOptions.members.map(member => (
                                <label key={member.id} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={formData.tags.members.includes(member.id)}
                                    onChange={() => handleTagToggle('members', member.id)}
                                    className="mr-2 rounded"
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-300">{member.name}</span>
                                </label>
                              ))
                            ) : (
                              <p className="text-sm text-gray-500 dark:text-gray-400">No members available</p>
                            )}
                          </div>
                        </div>

                        {/* Years */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Years <span className="text-orange-500">(recommended)</span>
                          </label>
                          <div className="space-y-2">
                            <input
                              type="number"
                              min="2000"
                              max={new Date().getFullYear() + 1}
                              placeholder="Add year"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleYearAdd((e.target as HTMLInputElement).value);
                                  (e.target as HTMLInputElement).value = ''
                                }
                              }}
                              className="w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-rb-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                            <div className="flex flex-wrap gap-1">
                              {formData.tags.years.map(year => (
                                <span
                                  key={year}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-rb-gold text-rb-dark text-xs rounded-full"
                                >
                                  {year}
                                  <button
                                    onClick={() => handleTagToggle('years', year)}
                                    className="hover:bg-yellow-400 rounded-full p-0.5"
                                  >
                                    <XMarkIcon className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-8">
                        <div className="text-center">
                          <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-2" />
                          <p className="text-gray-600 dark:text-gray-400">Failed to load tag options</p>
                          <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">You can still upload without tags</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {currentStep === 'uploading' && (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    Uploading Files...
                  </h4>
                  {uploads.map((upload) => (
                    <div key={upload.fileName} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {upload.fileName}
                        </span>
                        <div className="flex items-center gap-2">
                          {upload.status === 'success' && (
                            <CheckCircleIcon className="w-5 h-5 text-green-500" />
                          )}
                          {upload.status === 'error' && (
                            <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
                          )}
                          <span className="text-sm text-gray-500">
                            {upload.status === 'uploading' && `${Math.round(upload.progress)}%`}
                            {upload.status === 'processing' && 'Processing...'}
                            {upload.status === 'success' && 'Complete'}
                            {upload.status === 'error' && 'Failed'}
                          </span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            upload.status === 'success' ? 'bg-green-500' :
                            upload.status === 'error' ? 'bg-red-500' : 'bg-rb-gold'
                          }`}
                          style={{ width: `${upload.progress}%` }}
                        />
                      </div>
                      {upload.error && (
                        <p className="text-sm text-red-500">{upload.error}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                {currentStep !== 'upload' && currentStep !== 'uploading' && (
                  <button
                    onClick={() => setCurrentStep('upload')}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Back
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3">
                {currentStep !== 'uploading' && (
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                )}

                {currentStep === 'upload' && (
                  <button
                    onClick={() => setCurrentStep('details')}
                    disabled={!canGoNext}
                    className="px-4 py-2 bg-rb-gold text-rb-dark rounded-lg hover:bg-yellow-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                )}

                {currentStep === 'details' && (
                  <button
                    onClick={handleUpload}
                    disabled={!canGoNext}
                    className="px-4 py-2 bg-rb-gold text-rb-dark rounded-lg hover:bg-yellow-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Upload
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
